import type { FilterFn, Row } from '@tanstack/react-table';

/**
 * Built-in filter functions for common use cases.
 */

/**
 * Text search filter - case-insensitive substring match.
 */
export const textSearchFilter: FilterFn<unknown> = <TData>(
  row: Row<TData>,
  columnId: string,
  filterValue: string
): boolean => {
  const value = row.getValue(columnId);
  if (value === null || value === undefined) return false;

  const searchValue = filterValue.toLowerCase();
  return String(value).toLowerCase().includes(searchValue);
};

/**
 * Select filter - case-insensitive match for single value.
 */
export const selectFilter: FilterFn<unknown> = <TData>(
  row: Row<TData>,
  columnId: string,
  filterValue: string
): boolean => {
  if (!filterValue) return true;

  const value = row.getValue(columnId);
  return String(value).toLowerCase() === filterValue.toLowerCase();
};

/**
 * Multi-select filter - case-insensitive check if value is in array of selected values.
 */
export const multiSelectFilter: FilterFn<unknown> = <TData>(
  row: Row<TData>,
  columnId: string,
  filterValue: string[]
): boolean => {
  if (!filterValue || filterValue.length === 0) return true;

  const value = String(row.getValue(columnId)).toLowerCase();
  return filterValue.some((filter) => filter.toLowerCase() === value);
};

/**
 * Date range filter - checks if date is within range.
 */
export const dateRangeFilter: FilterFn<unknown> = <TData>(
  row: Row<TData>,
  columnId: string,
  filterValue: { start?: Date; end?: Date }
): boolean => {
  if (!filterValue || (!filterValue.start && !filterValue.end)) return true;

  const value = row.getValue(columnId);
  if (!value) return false;

  const date = value instanceof Date ? value : new Date(value as string | number);
  if (isNaN(date.getTime())) return false;

  if (filterValue.start && date < filterValue.start) return false;
  if (filterValue.end && date > filterValue.end) return false;

  return true;
};

/**
 * Number range filter - checks if number matches equals, greaterThan, or lessThan criteria.
 */
export const numberRangeFilter: FilterFn<unknown> = <TData>(
  row: Row<TData>,
  columnId: string,
  filterValue: { equals?: number; greaterThan?: number; lessThan?: number }
): boolean => {
  if (
    !filterValue ||
    (filterValue.equals === undefined &&
      filterValue.greaterThan === undefined &&
      filterValue.lessThan === undefined)
  ) {
    return true;
  }

  const value = row.getValue(columnId);
  if (value === null || value === undefined) return false;

  const num = Number(value);
  if (isNaN(num)) return false;

  // Equals takes precedence - if set, only match exact value
  if (filterValue.equals !== undefined) {
    return num === filterValue.equals;
  }

  // Check greaterThan and lessThan bounds
  if (filterValue.greaterThan !== undefined && num <= filterValue.greaterThan) return false;
  if (filterValue.lessThan !== undefined && num >= filterValue.lessThan) return false;

  return true;
};

/**
 * Get the appropriate filter function for a filter type.
 */
export const getFilterFn = (filterType: string): FilterFn<unknown> | undefined => {
  switch (filterType) {
    case 'search':
      return textSearchFilter;
    case 'select':
      return selectFilter;
    case 'multi-select':
      return multiSelectFilter;
    case 'date-range':
      return dateRangeFilter;
    case 'number-range':
      return numberRangeFilter;
    default:
      return undefined;
  }
};
